import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Search, Loader2, ArrowLeft } from "lucide-react";
import type { Deal, DealItem } from "@/types/deal";

const DealLookup = () => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const lookupDeals = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!search.trim()) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("deals")
        .select("*, deal_items(*)")
        .or(`batch_code.ilike.%${search.trim()}%,name.ilike.%${search.trim()}%`);

      if (error) throw error;

      const mapped: Deal[] = (data || []).map((d: any) => ({
        id: d.id,
        batchCode: d.batch_code,
        name: d.name,
        approved: d.approved,
        createdAt: d.created_at,
        items: (d.deal_items || []).map((i: any) => ({
          id: i.id,
          name: i.name,
          platform: i.platform || "",
          imageUrl: i.image_url || "",
          photoUrl: i.photo_url,
          priceType: i.price_type,
          originalPrice: i.original_price,
          grade: i.grade,
          gradePrice: i.grade_price,
          upc: i.upc || "",
        })),
      }));

      setDeals(mapped);
      if (mapped.length === 0) {
        toast({ title: "No Results", description: "No deals found matching your search." });
      }
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Failed to search deals.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const openDeal = (deal: Deal) => {
    navigate("/deal", { state: { deal } });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate("/")} variant="outline" size="sm" className="font-mono text-xs">
            <ArrowLeft className="w-3 h-3 mr-1" /> BACK
          </Button>
          <h1 className="text-xl font-bold font-mono text-foreground">DEAL LOOKUP</h1>
        </div>

        <form onSubmit={lookupDeals} className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Batch code or deal name..."
            className="font-mono bg-card border-border focus:border-primary text-foreground placeholder:text-muted-foreground"
          />
          <Button type="submit" disabled={loading || !search.trim()} className="bg-primary text-primary-foreground font-mono shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </form>

        <div className="space-y-3">
          {deals.map((deal) => (
            <button
              key={deal.id}
              onClick={() => openDeal(deal)}
              className="w-full text-left p-4 bg-card rounded-lg border border-border hover:border-primary/50 transition-colors space-y-1"
            >
              <div className="flex items-center justify-between">
                <span className="font-bold font-mono text-foreground">{deal.name}</span>
                {deal.approved && <span className="text-xs font-mono text-primary">✅ APPROVED</span>}
              </div>
              <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                <span>{deal.batchCode}</span>
                <span>{deal.items.length} items • ${deal.items.reduce((s, i) => s + parseFloat(i.gradePrice), 0).toFixed(2)}</span>
              </div>
              <p className="text-[10px] font-mono text-muted-foreground">
                {new Date(deal.createdAt).toLocaleDateString()}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DealLookup;
